import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BiArrowBack, BiCheckCircle, BiXCircle, BiSend } from "react-icons/bi";
import { toast } from "react-toastify";

import {
  getJrDetail,
  sendJrToClient,
  approveJr,
  rejectJrItems,
  reviseJrActivity,
  updateJrQty,
  aksiJrApproval,
  clearDetail,
  jrV2Selector,
} from "./jrV2Slice";
import { BiEditAlt } from "react-icons/bi";
import { Colors } from "../../../assets/Colors";
import GeneralServiceSummary from "../output/GeneralServiceSummary";
import PortRentalSummary from "../output/PortRentalSummary";
import QtyInput, { formatQty } from "../output/QtyInput";

const STATUS_CONFIG = {
  "-1":  { label: "Draft",             bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"    },
  "99":  { label: "Menunggu Manifest", bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"    },
  "100": { label: "Menunggu Activity", bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500"  },
  "110": { label: "Revisi JR",         bg: "bg-amber-100",  text: "text-amber-700",  dot: "bg-amber-500"   },
  "150": { label: "Menunggu Approval", bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500"  },
  "199": { label: "Pending Client",    bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500"  },
  "200": { label: "Approved",          bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"   },
  "201": { label: "Partial Reject",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"     },
};

const ACTIVITY_LABELS = {
  PORT_SERVICES: "Port Services", RENTAL: "Rental",
  WASTE_MANAGEMENT_SERVICES: "Waste Management Services",
  AREA_RENTAL: "Area Rental", MANPOWER: "Manpower", GENERAL_SERVICE: "General Service",
};

// Aktivitas yang dikelola entri harian terstruktur di halaman Activity
// (PortRentalEntryEditor — tanggal, meter, dimensi, dst per baris), sama
// seperti EDITOR_ACTS/ENTRY_LINE_GROUPS di ActivityDetailV2.jsx. Ditampilkan
// lewat PortRentalSummary supaya detailnya SAMA persis dengan yang diisi
// approver di halaman Activity, bukan cuma Actual/Billed seperti kartu biasa.
const ENTRY_SUMMARY_ACTS = new Set([
  "PORT_SERVICES", "RENTAL", "AREA_RENTAL", "MANPOWER", "WASTE_MANAGEMENT_SERVICES",
]);
// Item pendukung (ActivityName kosong, dikenali dari LineGroup) yang juga
// dikelola entri terstruktur — masuk ke grup "OTHER" tapi bukan kartu sederhana.
const ENTRY_LINE_GROUPS = new Set(["EQUIPMENT", "LABOUR", "TRUCK", "TWS"]);

function fmt(n) {
  if (!n && n !== 0) return "0";
  return new Intl.NumberFormat("id-ID").format(n);
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[String(status)] ?? STATUS_CONFIG["-1"];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function ItemStatusBadge({ status }) {
  const map = {
    pending:  { label: "Pending",  cls: "bg-gray-100 text-gray-500" },
    approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
    rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  };
  const c = map[status] ?? map.pending;
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

export default function JobRequestDetailV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const jrID = location.state?.JrID ?? null;

  const jrV2 = useSelector(jrV2Selector);
  const data = jrV2.detail;

  // Revisi Activity hanya boleh dilakukan RKS, bukan client (login Customer).
  // Dideklarasikan di atas early-return karena tombol "Kembali ke daftar" pada
  // cabang "JR tidak ditemukan" juga memakainya.
  const isClient = JSON.parse(localStorage.getItem("user") || "{}")?.Tipe === "Customer";
  // Daftar JR client ada di "/" — rute "/operasional-job-request-list" TIDAK
  // terdaftar untuk Tipe=Customer di App.js, jadi mengarahkannya ke sana
  // membuat halaman kosong.
  const jrListPath = isClient ? "/" : "/operasional-job-request-list";

  const [actionLoading, setActionLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectMap, setRejectMap] = useState({}); // { JrLineID: reason }
  const [rejectSel, setRejectSel] = useState({}); // { JrLineID: true } item yang dicentang untuk ditolak
  const [qtyMode, setQtyMode] = useState(false);
  const [qtyMap, setQtyMap]   = useState({});     // { JrLineID: actualQty }

  useEffect(() => {
    if (jrID) dispatch(getJrDetail(jrID));
    return () => { dispatch(clearDetail()); };
  }, [jrID]); // eslint-disable-line

  if (!jrID) {
    return (
      <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <p className="mb-2">JR tidak ditemukan</p>
          <button onClick={() => navigate(jrListPath)}
            className="text-sm text-orange-500 underline">Kembali ke daftar</button>
        </div>
      </div>
    );
  }

  if (jrV2.isDetailLoading || !data) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">Memuat data…</div>;
  }

  const reload = () => dispatch(getJrDetail(jrID));

  // ── Actions ──
  const handleSendToClient = async () => {
    setActionLoading(true);
    try {
      await dispatch(sendJrToClient(jrID)).unwrap();
      toast.success("JR dikirim ke client");
      reload();
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal mengirim"); }
    finally { setActionLoading(false); }
  };

  const handleApproveAll = async () => {
    const ok = window.confirm(
      `Yakin MENYETUJUI SEMUA item Job Request ${jrID}?\n\nTindakan tidak dapat dibatalkan.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      await dispatch(approveJr(jrID)).unwrap();
      toast.success("Semua item JR disetujui");
      reload();
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal approve"); }
    finally { setActionLoading(false); }
  };

  // ── Approval internal RKS (150 → setuju 199 / tolak 100) ──
  // Submit-nya ada di halaman Activity, bukan di sini.
  const handleAksiApproval = async (aksi) => {
    let alasan = "";
    if (aksi === "reject") {
      alasan = (window.prompt("Alasan penolakan (wajib diisi):") ?? "").trim();
      if (!alasan) { toast.error("Alasan penolakan wajib diisi"); return; }
    }
    const ok = window.confirm(
      aksi === "reject"
        ? `Yakin MENOLAK Job Request ${jrID}?\n\nAlasan: ${alasan}\n\nJR akan dikembalikan ke tahap Menunggu Activity.`
        : `Yakin MENYETUJUI (Approve) Job Request ${jrID}?\n\nSetelah ini JR diteruskan ke client dan tidak dapat dibatalkan.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      await dispatch(aksiJrApproval({ JrID: jrID, Aksi: aksi, Alasan: alasan })).unwrap();
      toast.success(aksi === "approve" ? "JR disetujui" : "JR ditolak, dikembalikan ke tahap Activity");
      reload();
    } catch (e) { toast.error(e?.message ?? "Gagal menyimpan approval"); }
    finally { setActionLoading(false); }
  };

  const handleReviseActivity = async () => {
    setActionLoading(true);
    try {
      await dispatch(reviseJrActivity(jrID)).unwrap();
      toast.success("JR dikembalikan ke tahap Activity untuk revisi");
      navigate("/operasional-activity-detail-v2", { state: { JrID: jrID } });
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal mengembalikan ke Activity"); }
    finally { setActionLoading(false); }
  };

  const handleSubmitReject = async () => {
    const checkedIds = Object.keys(rejectSel).filter(id => rejectSel[id]);
    if (checkedIds.length === 0) { toast.error("Centang minimal satu item untuk ditolak"); return; }
    const missingReason = checkedIds.some(id => !(rejectMap[id] && rejectMap[id].trim()));
    if (missingReason) { toast.error("Isi alasan untuk setiap item yang dicentang"); return; }
    const items = checkedIds.map(id => ({ JrLineID: parseInt(id), RejectReason: rejectMap[id].trim() }));
    const ok = window.confirm(
      `Yakin MENOLAK ${items.length} item pada Job Request ${jrID}?\n\nTindakan tidak dapat dibatalkan.`
    );
    if (!ok) return;
    setActionLoading(true);
    try {
      await dispatch(rejectJrItems({ JrID: jrID, Items: items })).unwrap();
      toast.success(`${items.length} item ditolak`);
      setRejectMode(false);
      setRejectMap({});
      setRejectSel({});
      reload();
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal reject"); }
    finally { setActionLoading(false); }
  };

  // ── Mode isi kuantitas (RKS) ──
  const startQtyMode = () => {
    const init = {};
    (data.Lines ?? []).forEach(l => { init[l.JrLineID] = l.ActualQty ?? 0; });
    setQtyMap(init);
    setQtyMode(true);
  };

  const handleSaveQty = async () => {
    const items = Object.entries(qtyMap).map(([JrLineID, ActualQty]) => ({
      JrLineID: parseInt(JrLineID),
      ActualQty: parseFloat(ActualQty) || 0,
    }));
    setActionLoading(true);
    try {
      await dispatch(updateJrQty({ JrID: jrID, Items: items })).unwrap();
      toast.success("Kuantitas tersimpan");
      setQtyMode(false);
      reload();
    } catch (e) { toast.error(typeof e === "string" ? e : "Gagal simpan qty"); }
    finally { setActionLoading(false); }
  };

  // ── Group lines by activity ──
  const grouped = (data.Lines ?? []).reduce((acc, l) => {
    const k = l.ActivityName || "OTHER";
    (acc[k] = acc[k] || []).push(l);
    return acc;
  }, {});

  // Tahap Pending Client adalah keputusan CLIENT. Setelah approver internal
  // menyetujui, RKS tak lagi punya tombol Setujui/Tolak di sini — kalau tidak,
  // RKS bisa menyetujui atas nama client tanpa client pernah melihatnya.
  const isPendingClient = data.Status === 199 && isClient;
  // Approval internal: hanya RKS yang mengirim, dan hanya approver yang sedang
  // giliran yang boleh menyetujui/menolak (backend memeriksa ulang hal yang sama).
  const approvalList  = data.ApprovalList ?? [];
  const userNik       = JSON.parse(localStorage.getItem("user") || "{}")?.Nik;
  const isMenungguApproval = data.Status === 150;
  const giliranSaya   = isMenungguApproval &&
    approvalList.some(a => a.Status === "menunggu" && a.Nik === userNik);

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate(jrListPath)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge status={data.Status} />

          {/* Tombol "Isi Kuantitas" & "Kirim ke Client" disembunyikan dulu
          {data.Status === 100 && !qtyMode && (
            <>
              <button onClick={startQtyMode} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium"
                style={{ borderColor: Colors.primary, color: Colors.primary }}>
                <BiEditAlt className="text-base" /> Isi Kuantitas
              </button>
              <button onClick={handleSendToClient} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold"
                style={{ background: Colors.primary }}>
                <BiSend /> Kirim ke Client
              </button>
            </>
          )}
          */}
          {data.Status === 100 && qtyMode && (
            <>
              <button onClick={() => setQtyMode(false)} disabled={actionLoading}
                className="px-4 py-1.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleSaveQty} disabled={actionLoading}
                className="px-4 py-1.5 rounded-lg text-white text-sm font-semibold"
                style={{ background: Colors.primary }}>
                Simpan Kuantitas
              </button>
            </>
          )}

          {/* Submit ke approval dilakukan dari halaman Activity
              (/operasional-activity-detail-v2), satu pintu — di sanalah hasil
              activity diisi, jadi qty tersimpan lebih dulu sebelum disubmit. */}

          {/* Menunggu Approval → hanya approver yang sedang giliran */}
          {giliranSaya && (
            <>
              <button onClick={() => handleAksiApproval("reject")} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-50">
                <BiXCircle className="text-base" /> Tolak
              </button>
              <button onClick={() => handleAksiApproval("approve")} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                <BiCheckCircle className="text-base" /> Setujui
              </button>
            </>
          )}

          {/* Pending Client → Approve / Reject */}
          {isPendingClient && !rejectMode && (
            <>
              <button onClick={() => setRejectMode(true)} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50">
                <BiXCircle className="text-base" /> Tolak Item
              </button>
              <button onClick={handleApproveAll} disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700">
                <BiCheckCircle className="text-base" /> Setujui Semua
              </button>
            </>
          )}
          {isPendingClient && rejectMode && (
            <>
              <button onClick={() => { setRejectMode(false); setRejectMap({}); setRejectSel({}); }} disabled={actionLoading}
                className="px-4 py-1.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
                Batal
              </button>
              <button onClick={handleSubmitReject} disabled={actionLoading}
                className="px-4 py-1.5 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
                Submit Penolakan
              </button>
            </>
          )}

          {/* Partial Reject → hanya RKS (bukan client) yang bisa kembalikan ke Activity untuk revisi */}
          {data.Status === 201 && !isClient && (
            <button onClick={handleReviseActivity} disabled={actionLoading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold"
              style={{ background: Colors.primary }}>
              <BiEditAlt className="text-base" /> Revisi Activity
            </button>
          )}
        </div>
      </div>

      {/* ── Rantai approval internal (aktor dari Setup › Approval, bukan hardcode) ── */}
      {approvalList.length > 0 && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">Approval Job Request</h3>
          <div className="flex flex-wrap gap-2">
            {approvalList.map((a) => {
              const gaya = {
                disetujui: { ikon: "✅", cls: "bg-green-50 border-green-200 text-green-800" },
                ditolak:   { ikon: "❌", cls: "bg-red-50 border-red-200 text-red-800" },
                menunggu:  { ikon: "⏳", cls: "bg-amber-50 border-amber-200 text-amber-800" },
                antre:     { ikon: "⚪", cls: "bg-gray-50 border-gray-200 text-gray-500" },
              }[a.Status] ?? { ikon: "⚪", cls: "bg-gray-50 border-gray-200 text-gray-500" };
              return (
                <div key={a.Urutan} className={`px-3 py-2 rounded-lg border text-sm ${gaya.cls}`}>
                  <span className="mr-1.5">{gaya.ikon}</span>
                  <span className="font-medium">{a.Urutan}. {a.NamaKaryawan || a.Nik}</span>
                  {a.Tanggal && <span className="ml-2 text-xs opacity-75">{a.Tanggal}</span>}
                  {a.Alasan && <div className="text-xs mt-0.5 italic">Alasan: {a.Alasan}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Header info ── */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <InfoField label="No. JR"        value={data.JrID} />
          <InfoField label="No. QO"        value={data.QoNo} />
          <InfoField label="Nama Klien"    value={data.NamaKlien} />
          <InfoField label="CQ"            value={data.KodeCQ ? `${data.KodeCQ}${data.NamaCQ ? " — " + data.NamaCQ : ""}` : ""} />
          <InfoField label="No. Kontrak"   value={data.NoKontrak} />
          <InfoField label="Tgl Pekerjaan" value={data.WorkDate} />
          <InfoField label="SPK No"        value={data.SPKNo} />
          <InfoField label="WSB No"        value={data.WSBNo} />
          <InfoField label="Prioritas"     value={data.Priority} />
          <InfoField label="Request Unit"  value={data.RequestUnit} />
          <InfoField label="Permit To Work" value={data.PermitToWork} />
          <InfoField label="Versi"         value={`Rev ${data.Revision ?? 0}`} />
        </div>
        {(data.Revisions ?? []).length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Riwayat Recall</p>
              <button onClick={() => navigate("/jr-version-compare", { state: { JrID: data.JrID } })}
                className="text-xs font-semibold text-orange-600 border border-orange-300 rounded px-2.5 py-1 hover:bg-orange-50">
                Bandingkan Versi
              </button>
            </div>
            <ul className="space-y-1 text-sm text-gray-700">
              {data.Revisions.map(r => (
                <li key={r.Revision}>
                  <span className="font-semibold">Rev {r.Revision} → Rev {r.Revision + 1}</span>
                  <span className="text-gray-500"> · {r.RecalledAt} · {r.RecalledBy}</span>
                  <span> — {r.Reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Total Tagihan disembunyikan dulu
        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Total Tagihan</p>
          <p className="text-lg font-bold" style={{ color: Colors.primary }}>Rp {fmt(data.TotalBilled)}</p>
        </div>
        */}
      </div>

      {/* ── Reject mode banner ── */}
      {rejectMode && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          Mode penolakan: <strong>centang</strong> item yang ingin ditolak lalu isi alasannya, kemudian klik <strong>Submit Penolakan</strong>. Item yang tidak dicentang akan dianggap disetujui.
        </div>
      )}

      {/* ── Qty mode banner ── */}
      {qtyMode && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-700">
          Mode isi kuantitas: masukkan jumlah aktual lapangan per layanan, lalu klik <strong>Simpan Kuantitas</strong>. Tagihan = MAX(actual, min usage) × rate.
        </div>
      )}

      {/* ── Lines per activity ── */}
      {Object.entries(grouped).map(([act, lines]) => {
        // General Service → tampilkan ringkasan (1 baris/layanan, expand untuk entri harian)
        if (act === "GENERAL_SERVICE") {
          return (
            <div key={act} className="mb-5">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
                  {ACTIVITY_LABELS[act] ?? act}
                </span>
                <span className="text-xs text-gray-500">{lines.length} item</span>
              </div>
              <GeneralServiceSummary lines={lines}
                rejectMode={rejectMode} rejectSel={rejectSel} setRejectSel={setRejectSel}
                rejectMap={rejectMap} setRejectMap={setRejectMap} />
            </div>
          );
        }
        // Port Services / Rental / Area Rental / Manpower / Waste → summary
        // (1 baris/service, expand → entri harian) — sama seperti General Service.
        if (ENTRY_SUMMARY_ACTS.has(act)) {
          return (
            <div key={act} className="mb-5">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
                  {ACTIVITY_LABELS[act] ?? act}
                </span>
                <span className="text-xs text-gray-500">{lines.length} item</span>
              </div>
              <PortRentalSummary lines={lines}
                rejectMode={rejectMode} rejectSel={rejectSel} setRejectSel={setRejectSel}
                rejectMap={rejectMap} setRejectMap={setRejectMap} />
            </div>
          );
        }
        // Sisanya ("OTHER"): item pendukung (Equipment/Labour/Truck/TWS) memakai
        // entri terstruktur yang sama seperti di atas — dipisah dari item
        // sederhana (qty tunggal) yang tetap tampil sebagai kartu ringkas.
        const entryLines  = lines.filter(l => ENTRY_LINE_GROUPS.has(l.LineGroup));
        const simpleLines = lines.filter(l => !ENTRY_LINE_GROUPS.has(l.LineGroup));
        return (
          <div key={act} className="mb-5">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
                {ACTIVITY_LABELS[act] ?? act}
              </span>
              <span className="text-xs text-gray-500">{lines.length} item</span>
            </div>
            {entryLines.length > 0 && (
              <div className="mb-3">
                <PortRentalSummary lines={entryLines}
                  rejectMode={rejectMode} rejectSel={rejectSel} setRejectSel={setRejectSel}
                  rejectMap={rejectMap} setRejectMap={setRejectMap} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {simpleLines.map(l => {
                const isFixed   = l.ServiceType === "F";
                const rejected  = l.ItemStatus === "rejected";
                const cardTone  = rejected ? "bg-red-50/50 border-red-200"
                                : isFixed  ? "bg-blue-50/40 border-blue-100"
                                : "bg-white border-gray-200";
                return (
                  <div key={l.JrLineID} className={`rounded-xl border p-4 ${cardTone}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <label className="flex items-start gap-2 font-semibold text-gray-800 text-sm leading-snug cursor-pointer">
                        {rejectMode && (
                          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-red-600 cursor-pointer"
                            checked={!!rejectSel[l.JrLineID]}
                            onChange={e => setRejectSel(s => ({ ...s, [l.JrLineID]: e.target.checked }))} />
                        )}
                        {l.ItemDisplay || l.ItemName}
                      </label>
                      <ItemStatusBadge status={l.ItemStatus} />
                    </div>

                    {qtyMode ? (
                      <div className="flex items-center gap-2 mb-2">
                        <label className="text-xs font-semibold uppercase text-gray-500 w-14">Actual</label>
                        <QtyInput
                          className="w-36 border-2 rounded-lg px-3 py-1.5 text-right font-medium outline-none focus:border-orange-400"
                          value={qtyMap[l.JrLineID] ?? 0}
                          onChange={v => setQtyMap(m => ({ ...m, [l.JrLineID]: v }))} />
                        <span className="text-sm text-gray-500">{l.SatuanID}</span>
                        <span className="text-xs text-gray-400 ml-auto">min {formatQty(l.MinUsage)}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                        <span>Actual: <strong className="text-gray-700">{formatQty(l.ActualQty)}</strong></span>
                        <span>Billed: <strong className="text-gray-700">{formatQty(l.BilledQty)}</strong></span>
                        <span>UoM: <strong className="text-gray-700">{l.SatuanNama || l.SatuanID || "—"}</strong></span>
                      </div>
                    )}

                    {/* Total rupiah per item disembunyikan dulu
                    <div className="border-t pt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-400">Total</span>
                      <span className="font-bold" style={{ color: Colors.primary }}>
                        Rp {qtyMode
                          ? fmt(Math.max(parseFloat(qtyMap[l.JrLineID]) || 0, l.MinUsage || 0) * (l.ContractRate || 0))
                          : fmt(l.LineAmount)}
                      </span>
                    </div>
                    */}

                    {/* Keterangan yang diketik di halaman Activity tersimpan per ENTRI
                        harian ("JrLineDetail"."Remarks") dan dirangkum backend jadi
                        EntryRemarks. Baris JR sendiri ("Remarks") tak punya form
                        pengisian, jadi ia hanya jadi pilihan pertama bila suatu saat
                        terisi. Alasan tolak punya kotaknya sendiri di bawah. */}
                    {(l.Remarks || l.EntryRemarks) && (
                      <div className="mb-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                        <span className="font-semibold uppercase text-[10px] text-gray-400 mr-1">Keterangan</span>
                        {l.Remarks || l.EntryRemarks}
                      </div>
                    )}

                    {rejected && l.RejectReason && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                        Alasan: {l.RejectReason}
                      </div>
                    )}

                    {rejectMode && rejectSel[l.JrLineID] && (
                      <input type="text" placeholder="Alasan tolak (wajib diisi)"
                        className="w-full mt-2 border border-red-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-red-500"
                        value={rejectMap[l.JrLineID] ?? ""}
                        onChange={e => setRejectMap(m => ({ ...m, [l.JrLineID]: e.target.value }))} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {data.Remarks && (
        <div className="bg-white rounded-xl border p-5 mt-2">
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-2">Keterangan</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.Remarks}</p>
        </div>
      )}
    </div>
  );
}
